import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import VendorScope3Form from "./VendorScope3Form";
import VendorStatusBadge from "./VendorStatusBadge";
import VendorActivityList from "./VendorActivityList";

const CATEGORIES = [
  { id: 1, label: "Purchased goods & services" },
  { id: 4, label: "Upstream transport" },
  { id: 5, label: "Waste treatment" },
  { id: 9, label: "Downstream transport" },
  { id: 10, label: "Processing of sold products" },
];

const VendorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportingYear, setReportingYear] = useState("FY2026");
  const [selectedCats, setSelectedCats] = useState(() => new Set([1, 4]));
  const [assigning, setAssigning] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [activity, setActivity] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  const loadActivity = async () => {
    try {
      const { data } = await axios.get(`/api/vendors/${id}/activity`);
      if (data.status && data.logs) setActivity(data.logs);
    } catch {
      setActivity([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/vendors/${id}`);
      if (data.status && data.vendor) {
        setVendor(data.vendor);
        const assignments = data.vendor.assignments || [];
        if (assignments.length && !selectedAssignmentId) {
          const first = assignments[0];
          setSelectedAssignmentId(first.id);
        }
      }
    } catch (e) {
      toastr.error(e.response?.data?.message || "Could not load vendor");
      navigate("/vendors");
    } finally {
      setLoading(false);
    }
    loadActivity();
  };

  useEffect(() => {
    setVendor(null);
    setSelectedAssignmentId(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when vendor id changes
  }, [id]);

  const sendInvite = async () => {
    setSendingInvite(true);
    try {
      const { data } = await axios.post(`/api/vendors/${id}/invite`, {});
      toastr.success(data.message || "Invitation sent");
      loadActivity();
    } catch (e) {
      toastr.error(e.response?.data?.message || "Could not send invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  const toggleCat = (cid) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  };

  const assignTemplates = async (e) => {
    e.preventDefault();
    const scope3Categories = Array.from(selectedCats);
    if (scope3Categories.length === 0) {
      toastr.warning("Select at least one category");
      return;
    }
    setAssigning(true);
    try {
      const { data } = await axios.post(`/api/vendors/${id}/assignments`, {
        reportingYear,
        scope3Categories,
      });
      if (data.status) {
        toastr.success("Assignments updated");
        const { data: v2 } = await axios.get(`/api/vendors/${id}`);
        setVendor(v2.vendor);
        const list = v2.vendor.assignments || [];
        if (list.length) setSelectedAssignmentId(list[list.length - 1].id);
      }
    } catch (err) {
      toastr.error(err.response?.data?.message || "Could not assign templates");
    } finally {
      setAssigning(false);
    }
  };

  if (loading || !vendor) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  const assignments = vendor.assignments || [];

  return (
    <div className="container-fluid py-4">
      <nav className="mb-3" aria-label="Breadcrumb">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
          onClick={() => navigate("/vendors")}
          aria-label="Back to vendors list"
        >
          <i className="bi bi-arrow-left" aria-hidden="true" />
          <span>Back to vendors</span>
        </button>
      </nav>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 className="h3 mb-0">{vendor.name}</h1>
          <p className="text-muted mb-0">{vendor.email}</p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <VendorStatusBadge status={vendor.status} variant="header" />
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={sendInvite}
            disabled={sendingInvite}
          >
            {sendingInvite ? "Sending…" : "Send invitation email"}
          </button>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6 card-title">Assign templates</h2>
              <form onSubmit={assignTemplates}>
                <div className="mb-2">
                  <label className="form-label small">Reporting year</label>
                  <input
                    className="form-control form-control-sm"
                    value={reportingYear}
                    onChange={(e) => setReportingYear(e.target.value)}
                    placeholder="FY2026"
                  />
                </div>
                <div className="mb-2 small">Categories</div>
                <div className="d-flex flex-column gap-1 mb-3">
                  {CATEGORIES.map((c) => (
                    <label key={c.id} className="form-check small mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedCats.has(c.id)}
                        onChange={() => toggleCat(c.id)}
                      />
                      <span className="form-check-label ms-1">
                        {c.id} — {c.label}
                      </span>
                    </label>
                  ))}
                </div>
                <button type="submit" className="btn btn-sm btn-success w-100" disabled={assigning}>
                  {assigning ? "Saving…" : "Add / confirm assignments"}
                </button>
              </form>

              <hr />

              <h2 className="h6">Assignments</h2>
              {assignments.length === 0 ? (
                <p className="small text-muted">None yet. Select categories above.</p>
              ) : (
                <div className="list-group list-group-flush">
                  {assignments.map((a) => (
                    <button
                      type="button"
                      key={a.id}
                      className={`list-group-item list-group-item-action small text-start d-flex justify-content-between align-items-center gap-2 ${
                        selectedAssignmentId === a.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedAssignmentId(a.id)}
                    >
                      <span className="text-truncate">
                        Cat {a.scope3Category} · {a.reportingYear}
                      </span>
                      <VendorStatusBadge status={a.status} variant="inline" />
                    </button>
                  ))}
                </div>
              )}

              <hr />
              <h2 className="h6 mb-2">Recent activity</h2>
              <p className="small text-muted mb-2">
                Timeline of invitations, sign-ins, saves, submissions, and calculations.
              </p>
              <VendorActivityList logs={activity} />
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h5 card-title">Disclosure form</h2>
              {selectedAssignmentId ? (
                <VendorScope3Form
                  assignmentId={selectedAssignmentId}
                  useVendorAuth={false}
                  onSaved={() => {
                    load();
                  }}
                />
              ) : (
                <p className="text-muted small">Select or create an assignment on the left.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPage;
